import { Modal } from 'antd';
import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  exportTemplateActions as actions,
  exportTemplateSelectors as selectors,
} from 'reducers/exportTemplate/reducer';
import { save as saveStrings } from '../strings';
import SaveTemplate from './SaveTemplate';
import TemplateEditor from './TemplateEditor';

/**
 * Modal for managing export templates (CRUD operations).
 * Allows users to create, view, edit, and delete export templates.
 */
function ExportTemplatesModal() {
  const dispatch = useDispatch();
  const isModalOpen = useSelector(selectors.isModalOpen);
  const modalView = useSelector(selectors.modalView);
  const isDraftDirty = useSelector(selectors.isDraftDirty);
  const isEditDirty = useSelector(selectors.isEditDirty);
  const saveTemplateRef = useRef(null);

  const showUnsavedChangesConfirm = onOk => {
    Modal.confirm({
      bodyStyle: { padding: 15 },
      title: saveStrings.unsavedChangesTitle,
      content: saveStrings.unsavedChangesContent,
      okText: saveStrings.unsavedChangesOk,
      cancelText: saveStrings.unsavedChangesCancel,
      onOk,
    });
  };

  const onModalClosing = () => {
    // When editing columns, step back to the tabs view instead of closing.
    // There may be unsaved sheet-level edits invisible from the column editor.
    if (modalView === 'editColumns') {
      if (isDraftDirty) {
        showUnsavedChangesConfirm(() => {
          dispatch(actions.columnEditingCancelled());
        });
        return;
      }
      dispatch(actions.columnEditingCancelled());
      return;
    }

    const saveFormDirty =
      (modalView === 'save' || modalView === 'settings') &&
      !!saveTemplateRef.current?.isFormDirty();

    if (isEditDirty || saveFormDirty) {
      showUnsavedChangesConfirm(() => {
        dispatch(actions.editModalClosing());
      });
      return;
    }

    dispatch(actions.editModalClosing());
  };

  const onBackFromSaveView = () => {
    if (saveTemplateRef.current?.isFormDirty()) {
      showUnsavedChangesConfirm(() => {
        dispatch(actions.saveViewExited());
      });
      return;
    }
    dispatch(actions.saveViewExited());
  };

  const onModalClosed = () => dispatch(actions.editModalClosed());

  return (
    <Modal
      data-testid='et-modal'
      open={isModalOpen}
      onCancel={onModalClosing}
      afterClose={onModalClosed}
      footer={null}
      width={550}
      maskClosable={false}
      destroyOnClose
      style={{ padding: 0 }}
      bodyStyle={{ padding: 0 }}
    >
      {(modalView === 'edit' || modalView === 'editColumns') && (
        <TemplateEditor />
      )}
      {(modalView === 'save' || modalView === 'settings') && (
        <SaveTemplate
          ref={saveTemplateRef}
          onCancel={onModalClosing}
          onBack={onBackFromSaveView}
        />
      )}
    </Modal>
  );
}

export default ExportTemplatesModal;
